/**
 * Redesigned comment composer for Word Add-in task pane (320px).
 *
 * Layout (top → bottom):
 *   1. Anchor preview (if selected)
 *   2. Pending files preview (if any)
 *   3. Markdown preview (if toggled)
 *   4. Visibility pill bar (Intern / Alle / Kun …)
 *   5. Toolbar row: anchor + attach + preview toggle
 *   6. Textarea + send button
 *
 * Compact mode (inline replies): single-row input, no toolbar.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Send, MapPin, Paperclip, X, Eye, Type, BookOpen } from 'lucide-react'
import { useCreateComment } from '../../hooks/useComments'
import { addAttachment, sendTypingIndicator } from '../../services/commentsApi'
import { getSelectedTextForAnchor, insertCommentControl } from '../../hooks/useCommentControls'
import { useSelectionAnchor } from '../../hooks/useSelectionAnchor'
import { useCommentDraft } from '../../hooks/useCommentDraft'
import { devLog } from '../../services/devLog'
import type { NegotiationParty } from '../../services/negotiationsApi'
import { MentionAutocomplete, useMentionAutocomplete } from './MentionAutocomplete'
import { ClausePicker } from './ClausePicker'
import { useClauseInsert } from '../../hooks/useClauseInsert'
import { useParagraphMap } from '../../hooks/useParagraphMap'
import type { Clause } from '../../services/clausesApi'

interface CommentComposeProps {
  negotiationId: number
  currentVersion: number
  parties: NegotiationParty[]
  actorId?: string
  /** Default visibility from ModeSelector: 'INTERNAL' | 'ALL' | 'SPECIFIC:{tag}' */
  defaultVisibility?: string
  onCommentCreated?: () => void
  /** Parent comment ID for replies */
  parentCommentId?: number
  /** Compact mode for inline replies (1-row textarea, no anchor/attachment) */
  compact?: boolean
  /** Toast callback for clause insert feedback */
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void
  /** Cancel callback for compact reply mode */
  onCancel?: () => void
}

type VisibilityOption = 'INTERNAL' | 'ALL' | 'SPECIFIC'

export function CommentCompose({
  negotiationId,
  currentVersion,
  parties,
  actorId,
  defaultVisibility,
  onCommentCreated,
  parentCommentId,
  compact = false,
  showToast,
  onCancel,
}: CommentComposeProps) {
  const { t } = useTranslation()
  const createComment = useCreateComment()

  // Draft persistence — saves to localStorage so comments survive accidental closes
  const draft = useCommentDraft(negotiationId, compact ? undefined : parentCommentId, actorId)
  const [compactContent, setCompactContent] = useState('')
  const content = compact ? compactContent : draft.content
  const setContent = compact ? setCompactContent : draft.setContent
  const [visibility, setVisibility] = useState<VisibilityOption>('INTERNAL')
  const [targetTag, setTargetTag] = useState('')
  const [showVisHint, setShowVisHint] = useState(() => {
    try { return !localStorage.getItem('kobsign-visibility-onboarding-seen') } catch { return false }
  })
  const { getParaId } = useParagraphMap(negotiationId, currentVersion)
  const { anchor, isAutoDetected, setManualAnchor, clearAnchor } = useSelectionAnchor(!compact)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [sendError, setSendError] = useState<string | null>(null)
  const [cursorPosition, setCursorPosition] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const [showClausePicker, setShowClausePicker] = useState(false)
  const { insertClause, isInserting } = useClauseInsert()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // @mention autocomplete state
  const mention = useMentionAutocomplete(content, cursorPosition)
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 })

  // Typing indicator: debounce 2s, stop after 5s inactivity
  const lastTypingRef = useRef(0)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const emitTypingIndicator = useCallback(() => {
    const now = Date.now()
    if (now - lastTypingRef.current < 2000) return
    lastTypingRef.current = now
    sendTypingIndicator(negotiationId).catch(() => {})
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      lastTypingRef.current = 0
    }, 5000)
  }, [negotiationId])

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    }
  }, [])

  // Track whether user has manually overridden the mode selector
  const userOverrideRef = useRef(false)

  // Sync from defaultVisibility prop
  useEffect(() => {
    if (!defaultVisibility) return
    userOverrideRef.current = false

    /* eslint-disable react-hooks/set-state-in-effect -- syncing derived state from prop is intentional */
    if (defaultVisibility.startsWith('SPECIFIC:')) {
      setVisibility('SPECIFIC')
      setTargetTag(defaultVisibility.slice(9))
    } else if (defaultVisibility === 'ALL' || defaultVisibility === 'INTERNAL') {
      setVisibility(defaultVisibility)
      setTargetTag('')
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [defaultVisibility])

  // Get the current actor's party color for the CC marker
  const myParty = parties.find((p) => p.actor?.koblink_id === actorId)
  const myColor = myParty?.color || '#ADB5BD'
  const isSilentReviewer = myParty?.is_silent === true

  // Build visibility options from parties
  const counterparties = parties.filter(
    (p) => (p.role === 'COUNTERPARTY' || p.role === 'CUSTOMER') && p.is_active,
  )

  const handleAnchor = useCallback(async () => {
    const selected = await getSelectedTextForAnchor()
    if (selected) {
      setManualAnchor(selected)
    }
  }, [setManualAnchor])

  const handleClauseSelect = useCallback((clause: Clause) => {
    // Append clause body to comment text
    setContent((prev: string) => prev ? `${prev}\n\n${clause.body}` : clause.body)
    setShowClausePicker(false)
  }, [setContent])

  const handleClauseInsertToDocument = useCallback(async (clause: Clause) => {
    const success = await insertClause(clause)
    if (success) {
      showToast?.('success', t('negotiate.clauseInsert.success'))
      setShowClausePicker(false)
    } else {
      showToast?.('error', t('negotiate.clauseInsert.error'))
    }
  }, [insertClause, showToast, t])

  const handleSend = async () => {
    if (!content.trim()) return
    setSendError(null)

    try {
      const comment = await createComment.mutateAsync({
        negotiationId,
        content: content.trim(),
        visibility,
        target_tag: visibility === 'SPECIFIC' ? targetTag : undefined,
        parent_comment_id: parentCommentId,
        anchor_text: compact ? undefined : anchor?.text,
        anchor_paragraph: compact ? undefined : anchor?.paragraphIndex,
        para_id: compact ? undefined : getParaId(anchor?.paragraphIndex),
      })

      if (anchor && comment.word_cc_tag) {
        try {
          await insertCommentControl(comment.word_cc_tag, anchor.text, myColor)
        } catch {
          setSendError(t('comments.anchorFailed', 'Comment sent, but document marker failed'))
        }
      }

      if (pendingFiles.length > 0) {
        const failedFiles: string[] = []
        for (const file of pendingFiles) {
          try {
            await addAttachment(negotiationId, comment.id, file)
          } catch (err) {
            devLog.warn('CommentCompose', `Failed to upload attachment: ${err}`)
            failedFiles.push(file.name)
          }
        }
        if (failedFiles.length > 0) {
          setSendError(t('comments.attachmentFailed', 'Failed to upload: {{files}}', { files: failedFiles.join(', ') }))
        }
      }

      if (compact) {
        setCompactContent('')
      } else {
        draft.clearDraft()
      }
      clearAnchor()
      setPendingFiles([])
      onCommentCreated?.()
    } catch (err) {
      devLog.warn('CommentCompose', `Failed to send comment: ${err}`)
      setSendError(t('comments.sendFailed', 'Could not send comment. Try again?'))
    }
  }

  const canSend = content.trim().length > 0 && !createComment.isPending

  // Auto-resize textarea and track cursor
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value)
    setCursorPosition(e.target.selectionStart || 0)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 100) + 'px'
    if (e.target.value.trim()) {
      emitTypingIndicator()
    }
  }

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.target as HTMLTextAreaElement
    setCursorPosition(el.selectionStart || 0)
    if (textareaRef.current && containerRef.current) {
      const rect = textareaRef.current.getBoundingClientRect()
      const containerRect = containerRef.current.getBoundingClientRect()
      setMentionPosition({
        top: rect.bottom - containerRect.top + 2,
        left: 8,
      })
    }
  }

  const handleMentionSelect = useCallback((party: NegotiationParty) => {
    const before = content.slice(0, mention.mentionStart)
    const after = content.slice(cursorPosition)
    const mentionText = `@${party.tag_name} `
    const newContent = before + mentionText + after
    setContent(newContent)
    const newPosition = mention.mentionStart + mentionText.length
    setCursorPosition(newPosition)
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newPosition, newPosition)
      }
    }, 0)
  }, [content, cursorPosition, mention.mentionStart, setContent])

  // --- Visibility helpers ---
  const setVis = useCallback((v: VisibilityOption, tag = '') => {
    userOverrideRef.current = true
    setVisibility(v)
    setTargetTag(tag)
  }, [])

  const isInternal = visibility === 'INTERNAL'
  const isAll = visibility === 'ALL'
  const isSpecific = visibility === 'SPECIFIC'
  const specificParty = counterparties.find((p) => p.tag_name === targetTag)

  const pillBase = 'px-2.5 py-1 text-[10px] font-medium rounded-full transition-all border'

  // =====================
  // Compact reply mode
  // =====================
  if (compact) {
    return (
      <div className="px-2 py-1.5 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg mx-2 mb-0.5">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('comments.replyPlaceholder')}
            className="flex-1 px-2 py-1 text-[10px] border border-neutral-200 dark:border-neutral-600 rounded-full bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500 placeholder:text-neutral-500"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && canSend) {
                e.preventDefault()
                handleSend()
              }
              if (e.key === 'Escape') {
                onCancel?.()
              }
            }}
          />
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 hover:scale-105 active:scale-95"
            title={t('comments.send')}
          >
            <Send size={9} />
          </button>
          <button
            onClick={onCancel}
            className="p-0.5 rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            title={t('comments.cancelReply')}
          >
            <X size={9} />
          </button>
        </div>
      </div>
    )
  }

  // =====================
  // Full compose mode
  // =====================
  return (
    <div ref={containerRef} className="shrink-0 border-t border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 relative">
      {/* Anchor preview — auto-detected from Word selection or manually set */}
      {anchor && (
        <div className={`mx-3 mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border ${
          isAutoDetected
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
        }`}>
          <MapPin size={12} className={`shrink-0 ${isAutoDetected ? 'text-blue-500' : 'text-primary-500'}`} />
          <span className={`text-[10px] flex-1 truncate ${
            isAutoDetected
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-primary-700 dark:text-primary-300'
          }`}>
            &ldquo;{anchor.text.substring(0, 50)}&rdquo;
          </span>
          <button
            onClick={clearAnchor}
            className="shrink-0 p-0.5 rounded hover:bg-primary-100 dark:hover:bg-primary-900/40"
          >
            <X size={10} className={isAutoDetected ? 'text-blue-400' : 'text-primary-400'} />
          </button>
        </div>
      )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="px-3 pt-1.5 flex flex-wrap gap-1">
          {pendingFiles.map((file, i) => (
            <span
              key={`${file.name}-${i}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-[10px] text-neutral-600 dark:text-neutral-400"
            >
              <Paperclip size={9} />
              <span className="truncate max-w-[60px]">{file.name}</span>
              <button
                onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                className="ml-0.5 p-0.5 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700"
              >
                <X size={8} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Markdown preview */}
      {showPreview && content.trim() && (
        <div className="mx-3 mt-1.5 p-2 rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-[10px] text-neutral-700 dark:text-neutral-300 max-h-20 overflow-y-auto">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            allowedElements={['p', 'strong', 'em', 'del', 'a', 'ul', 'ol', 'li', 'code', 'br']}
            unwrapDisallowed
          >
            {content}
          </ReactMarkdown>
        </div>
      )}

      {/* Visibility onboarding hint */}
      {showVisHint && (
        <div className="mx-3 mt-1.5 flex items-start gap-1.5 px-2 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-[9px] text-blue-700 dark:text-blue-300">
          <span className="flex-1">
            {t('comments.visibilityOnboarding', 'Control who sees each comment: Internal (your team only), All parties, or a specific counterparty.')}
          </span>
          <button
            onClick={() => {
              setShowVisHint(false)
              try { localStorage.setItem('kobsign-visibility-onboarding-seen', '1') } catch { /* */ }
            }}
            className="shrink-0 font-medium hover:underline"
          >
            {t('common.gotIt', 'Got it')}
          </button>
        </div>
      )}

      {/* Silent reviewer warning */}
      {isSilentReviewer && visibility !== 'INTERNAL' && (
        <div className="mx-3 mt-1.5 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[9px] text-amber-700 dark:text-amber-300">
          <span className="flex-1">
            {t('comments.silentReviewerWarning', 'You are a silent reviewer. Posting as "All" or to a specific party will reveal your presence to counterparties.')}
          </span>
        </div>
      )}

      {/* Visibility pills */}
      <div className="px-3 pt-2 pb-1 flex items-center gap-1 flex-wrap">
        <button
          onClick={() => setVis('INTERNAL')}
          title={t('comments.visibilityHelp.internal', 'Only your team can see this comment')}
          className={`${pillBase} ${
            isInternal
              ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700'
              : 'bg-transparent text-neutral-400 border-neutral-200 hover:border-neutral-400 dark:border-neutral-700 dark:hover:border-neutral-500'
          }`}
        >
          {t('comments.modeInternal')}
        </button>
        <button
          onClick={() => setVis('ALL')}
          title={t('comments.visibilityHelp.all', 'All parties can see this comment')}
          className={`${pillBase} ${
            isAll
              ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700'
              : 'bg-transparent text-neutral-400 border-neutral-200 hover:border-neutral-400 dark:border-neutral-700 dark:hover:border-neutral-500'
          }`}
        >
          {t('comments.modeAll')}
        </button>
        {counterparties.length > 0 && (
          <select
            value={isSpecific ? `SPECIFIC:${targetTag}` : ''}
            title={t('comments.visibilityHelp.specific', 'Choose which parties can see this')}
            onChange={(e) => {
              if (e.target.value) {
                setVis('SPECIFIC', e.target.value.slice(9))
              }
            }}
            className={`${pillBase} appearance-none pr-5 cursor-pointer ${
              isSpecific
                ? 'border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400'
                : 'bg-transparent text-neutral-400 border-neutral-200 dark:border-neutral-700'
            }`}
            style={
              isSpecific && specificParty
                ? {
                    backgroundColor: `${specificParty.color}15`,
                    borderColor: `${specificParty.color}60`,
                    color: specificParty.color,
                  }
                : undefined
            }
          >
            <option value="" disabled>
              {t('comments.modeSpecific', { party: '...' })}
            </option>
            {counterparties.map((p) => (
              <option key={p.tag_name} value={`SPECIFIC:${p.tag_name}`}>
                {p.party_label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Toolbar: anchor + attach + preview */}
      <div className="px-3 pb-1 flex items-center gap-1">
        <button
          onClick={handleAnchor}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
            anchor
              ? isAutoDetected
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
              : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          }`}
          title={anchor && isAutoDetected ? t('comments.anchorAutoHint', 'Selected text will be anchored') : t('comments.anchorHint')}
        >
          <MapPin size={13} />
          {anchor ? t('comments.anchored') : t('comments.anchorText')}
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
            pendingFiles.length > 0
              ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
              : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          }`}
          title={t('comments.addAttachment')}
        >
          <Paperclip size={13} />
          {pendingFiles.length > 0 ? `${pendingFiles.length}` : t('comments.attach')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            setPendingFiles((prev) => [...prev, ...files].slice(0, 3))
            e.target.value = ''
          }}
        />
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
            showPreview
              ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
              : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          }`}
          title={t('comments.preview', 'Preview')}
        >
          {showPreview ? <Eye size={13} /> : <Type size={13} />}
        </button>
        <div className="relative">
          <button
            onClick={() => setShowClausePicker((s) => !s)}
            disabled={isInserting}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
              showClausePicker
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800'
            }`}
            title={t('negotiate.clause.suggest', 'Suggest Clause')}
          >
            <BookOpen size={13} />
          </button>
          {showClausePicker && (
            <ClausePicker
              onSelect={handleClauseSelect}
              onClose={() => setShowClausePicker(false)}
              onInsertToDocument={handleClauseInsertToDocument}
            />
          )}
        </div>
      </div>

      {/* Send error */}
      {sendError && (
        <div className="mx-3 mb-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <span className="text-[10px] text-red-600 dark:text-red-400 flex-1">{sendError}</span>
          <button
            onClick={() => { setSendError(null); handleSend() }}
            className="text-[10px] font-medium text-red-600 dark:text-red-400 hover:underline shrink-0"
          >
            {t('comments.retry', 'Retry')}
          </button>
          <button
            onClick={() => setSendError(null)}
            className="shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40"
          >
            <X size={10} className="text-red-400" />
          </button>
        </div>
      )}

      {/* Textarea + send */}
      <div className="px-3 pb-2 flex items-end gap-1.5">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onSelect={handleSelect}
          onClick={handleSelect}
          onKeyDown={(e) => {
            if (mention.isOpen && (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
              return
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder={t('comments.compose')}
          rows={2}
          className="flex-1 px-3 py-2 text-[11px] leading-relaxed border border-neutral-200 dark:border-neutral-600 rounded-xl bg-neutral-50 dark:bg-neutral-800 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 placeholder:text-neutral-400"
          style={{ minHeight: '44px', maxHeight: '100px' }}
        />

        {/* @mention autocomplete */}
        {mention.isOpen && (
          <MentionAutocomplete
            parties={parties}
            query={mention.query}
            onSelect={handleMentionSelect}
            onClose={() => setCursorPosition(-1)}
            position={mentionPosition}
          />
        )}

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 hover:scale-105 active:scale-95 flex-shrink-0 mb-0.5"
          title={`${t('comments.send')} (Ctrl+Enter)`}
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  )
}
